import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TextField, Dialog, Button } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";

import axios from "../../utils/axios";
import { setDocument, setData } from "../../reducers/documento";
import "./style.css";

const SIN_CATEGORIA = "Otros";

// Para buscar sin importar mayúsculas ni tildes
const normalize = (text) =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// El buscador también encuentra por descripción y categoría, no solo por nombre
const filterTramites = (options, { inputValue }) => {
  const terms = normalize(inputValue).split(/\s+/).filter(Boolean);
  if (!terms.length) return options;
  return options.filter((t) => {
    const haystack = normalize(`${t._nameSchema} ${t._description} ${t._category}`);
    return terms.every((term) => haystack.includes(term));
  });
};

export default function HomepageSearch() {
  const dispatch = useDispatch();

  let history = useHistory();

  const [tramites, setTramites] = useState([]);
  const [selected, setSelected] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlerSelect = (e, value) => {
    setSelected(value);
    handleOpen();
  };

  const handleStep = async () => {
    try {
      handleClose();
      dispatch(setDocument(selected));
      //history.push(`/start/${selected.id}`);
      const { data: document } = await axios.post("/api/documento", {
        bpmn: selected.id,
      });
      dispatch(setData(document));
      history.push(`/procedure/${document.id}`);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data } = await axios.post("/api/tramites");
        setTramites(
          data.map((it) => {
            it.id = it._id;
            return it;
          })
        );
      } catch (e) {
        // Safari móvil aborta la petición al salir de la pestaña o perder red: no es un error de la app
        if (e.code !== "ECONNABORTED") console.error(e);
      }
    };
    loadData();
  }, []);

  const [showAll, setShowAll] = useState(false);
  const [category, setCategory] = useState("");

  // Categorías con su cantidad, de la más usada a la menos ("Otros" al final)
  const categories = useMemo(() => {
    const counts = {};
    tramites.forEach((t) => {
      const name = (t._category || "").trim() || SIN_CATEGORIA;
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => {
        if (a.name === SIN_CATEGORIA) return 1;
        if (b.name === SIN_CATEGORIA) return -1;
        return b.count - a.count || a.name.localeCompare(b.name, "es");
      });
  }, [tramites]);

  const filtered = category
    ? tramites.filter((t) => ((t._category || "").trim() || SIN_CATEGORIA) === category)
    : tramites;

  const VISIBLE = 9;
  const visibles = showAll ? filtered : filtered.slice(0, VISIBLE);

  if (tramites.length === 0) {
    return (
      <div id="homeInitial" className="gd-home">
        <p className="gd-home-hint">Cargando trámites…</p>
      </div>
    );
  }
  return (
    <div id="homeInitial" className="gd-home">
      <div className="gd-home-search">
        <label htmlFor="home-tramite">¿Qué trámite necesitas?</label>
        <Autocomplete
          id="home-tramite"
          className="input_gestdoc"
          options={filtered}
          filterOptions={filterTramites}
          getOptionLabel={(option) => option._nameSchema}
          onChange={handlerSelect}
          noOptionsText="Sin resultados"
          style={{ width: "100%" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Busca por nombre, por ejemplo: firma simple, contrato, NDA"
              variant="outlined"
            />
          )}
        />
        <p className="gd-home-hint">
          {tramites.length} trámites disponibles. Elige uno de la lista o búscalo por nombre o tema.
        </p>
      </div>

      {categories.length > 1 && (
        <div className="gd-home-chips" role="toolbar" aria-label="Filtrar por categoría">
          <button
            type="button"
            className={"gd-home-chip" + (category === "" ? " is-active" : "")}
            onClick={() => setCategory("")}
          >
            Todas <span>{tramites.length}</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.name}
              type="button"
              className={"gd-home-chip" + (category === c.name ? " is-active" : "")}
              onClick={() => setCategory(category === c.name ? "" : c.name)}
            >
              {c.name} <span>{c.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className={"gd-home-grid" + (showAll ? "" : " is-collapsed")}>
        {visibles.map((t) => (
          <button
            key={t._id}
            type="button"
            className="gd-home-item"
            onClick={() => handlerSelect(null, t)}
          >
            <span className="gd-home-item-icon" aria-hidden="true">
              <DescriptionOutlinedIcon fontSize="small" />
            </span>
            <span className="gd-home-item-name">{t._nameSchema}</span>
            <span className="gd-home-item-arrow" aria-hidden="true">→</span>
          </button>
        ))}
      </div>
      {filtered.length > VISIBLE && (
        <button type="button" className="gd-home-more" onClick={() => setShowAll(!showAll)}>
          {showAll ? "Ver menos" : `Ver los ${filtered.length} trámites`}
        </button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        classes={{ paper: "gd-confirm-dialog" }}
      >
        <div className="gd-confirm-header">
          <h4>Iniciar trámite</h4>
          <p>{selected ? selected._nameSchema : "Para continuar, debes escoger un documento."}</p>
        </div>
        <div className="gd-confirm-actions">
          <Button className="gd-btn gd-btn-ghost" onClick={handleClose}>
            Cancelar
          </Button>
          {selected && (
            <Button className="gd-btn gd-btn-primary" onClick={handleStep}>
              Continuar
            </Button>
          )}
        </div>
      </Dialog>
    </div>
  );
}
